import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { getPublicUser } from "../../services/api/UserDataService"
import { getPublicPage } from "../../services/api/PageDataService"
import { startSession, trackClick } from "../../services/analytics/receipts"
import { iconNameToCode } from "../../utils/socialIcons"
import { resolveMediaUrl } from "../../utils/media/resolveMediaUrl"
import UserNotFoundPage from '../notfound/UserNotFoundPage'
import BrowserRedirect from '../../utils/routing/BrowserRedirect'
import { useIsMobile } from '../../hooks/useIsMobile'
import PreviewBanner from '../../components/videopage/preview/PreviewBanner'
import { VibioPageMobile } from './VibioPageMobile'
import LoadingPage from './fillerpages/LoadingPage'
import VibioPageWeb from './VibioPageWeb'


export default function VideoPage() {
    const { username } = useParams()
    const [searchParams] = useSearchParams()
    const isPreview = searchParams.get('preview') === 'true'
    const forceMobile = searchParams.get('mobile') === 'true'

    const [user, setUser] = useState(null)
    const [pageId, setPageId] = useState(null)
    const [videoSrc, setVideoSrc] = useState(null)
    const [linkButtons, setLinkButtons] = useState([])
    const [socials, setSocials] = useState([])
    const [theme, setTheme] = useState(null)

    const [isLoading, setIsLoading] = useState(true)
    const detectedMobile = useIsMobile()
    const isMobile = forceMobile || detectedMobile


    useEffect(() => {
        const fetchPage = async () => {
            setIsLoading(true)
            try {
                // Profile card (name/bio/picture) and the bundled page body are separate endpoints.
                const [profile, page] = await Promise.all([
                    getPublicUser(username),
                    getPublicPage(username),
                ])
                setUser(profile)
                setPageId(page.pageId)
                setVideoSrc(resolveMediaUrl(page.videoSrc))
                setLinkButtons(page.links || [])
                // Backend sends icon as a NAME; SocialLinkList renders by numeric code.
                setSocials((page.socials || []).map((s) => ({ ...s, icon: iconNameToCode(s.icon) })))
                setTheme(page.theme)
            } catch (error) {
                console.error("Error fetching public page: ", error)
                setUser(null)
            } finally {
                setIsLoading(false)
            }
        }
        fetchPage()
    }, [username])

    // Anonymous Receipts analytics: start a visit (+ heartbeats) once the page loads. Skipped in
    // the owner's preview so creators don't inflate their own numbers.
    useEffect(() => {
        if (!pageId || isPreview) {
            return undefined
        }
        return startSession({ pageId })
    }, [pageId, isPreview])

    // Click tracking is disabled in preview (no session is started there).
    const onLinkClick = isPreview ? undefined : (targetType, targetId) => trackClick(pageId, targetType, targetId)


    const content = isLoading ? (
        <LoadingPage />
    ) : user ? (
        <div style={{ padding: 0, margin: 0 }}>
            {isMobile ? (
                <>
                    {isPreview && <PreviewBanner />}
                    <VibioPageMobile
                        videoSrc={videoSrc}
                        socials={socials}
                        linkButtons={linkButtons}
                        userData={user}
                        theme={theme}
                        onLinkClick={onLinkClick}
                    />
                </>
            ) : (
                <VibioPageWeb
                    videoSrc={videoSrc}
                    socials={socials}
                    linkButtons={linkButtons}
                    userData={user}
                    theme={theme}
                    onLinkClick={onLinkClick}
                />
            )}
        </div>
    ) : (
        <UserNotFoundPage />
    )


    return (
        <BrowserRedirect>{content}</BrowserRedirect>
    )
}
